import React, { useEffect, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useGetApi } from "../hooks/useGetApi";
import {
  addMedicineReturnApi,
  getMedicineRequestsApi,
} from "../services/medicine-tracking.service";
import { useNavigate, useParams } from "react-router-dom";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import Loader from "../components/Loader/loader";
import MedViewDetails from "../components/nursing/modals/med-view-details";
import { toast } from "react-toastify";

export default function NursingMedicineReturn() {
  const { patientId, visitId } = useParams();
  const navigate = useNavigate();

  const {
    dataList: allData,
    isLoading,
    isError,
    refetch,
  } = useGetApi({
    apiFunction: getMedicineRequestsApi,
    params: {
      limit: Number.MAX_SAFE_INTEGER,
      patientId: patientId,
      visitId: visitId,
      listFor: "return",
    },
    rawData: true,
  });

  const patientInfo = allData?.patientInfo || {};

  const [selectedRow4ReturnDetails, setSelectedRow4ReturnDetails] =
    useState(null);

  const [selectedRows, setSelectedRows] = useState([]); // it will select all the rows
  const [modalOpen, setModalOpen] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState(null);
  const [loading, setLoading] = useState(false);
  const [medReqTrackList, setMedReqTrackList] = useState([]);

  const handleInvoiceChange = (newValue) => {
    setInvoiceNumber(newValue);
  };

  const handleOpenModal = (row) => {
    setSelectedRow4ReturnDetails(row);
    setModalOpen(true);
  };

  const handleSelectAllClick = (event) => {
    const checked = event.target.checked;
    const updatedList = medReqTrackList?.map((row) => ({
      ...row,
      isSelected: checked,
    }));
    setMedReqTrackList(updatedList);
    setSelectedRows(checked ? updatedList : []);
  };

  const handleClick = (event, row) => {
    const updatedList = medReqTrackList?.map((r) =>
      r?.sNo === row?.sNo ? { ...r, isSelected: !r?.isSelected } : r
    );
    setMedReqTrackList(updatedList);
    setSelectedRows(updatedList?.filter((r) => r?.isSelected));
  };

  const handleReturnQuantityChange = (sNo, value, issuedQuantity) => {
    const issuedNum = Number(issuedQuantity);
    const maxIssued =
      Number.isFinite(issuedNum) && issuedNum >= 0 ? issuedNum : 0;

    const raw = value === "" ? 0 : Number(value);
    const num = Number.isFinite(raw) ? raw : 0;
    if (num < 0) {
      toast.error("Return quantity cannot be less than 0");
    }
    const clampedLow = Math.max(0, num);
    // Allow return quantity to equal issued quantity; only block strictly over.
    if (clampedLow > maxIssued) {
      toast.error("Return quantity cannot exceed issued quantity");
      return;
    }
    const updatedList = medReqTrackList?.map((row) =>
      row?.sNo === sNo
        ? {
            ...row,
            returnQuantity: Math.min(clampedLow, maxIssued),
          }
        : row
    );

    setMedReqTrackList(updatedList);
    setSelectedRows(updatedList?.filter((r) => r?.isSelected));
  };

  const handleReturnMedicine = async () => {
    const groupedByTrackingId = {};

    selectedRows?.forEach((row) => {
      if (row?.returnQuantity > 0) {
        if (!groupedByTrackingId?.[row?.trackingId]) {
          groupedByTrackingId[row?.trackingId] = [];
        }
        groupedByTrackingId[row?.trackingId].push({
          medicineId: row?.medicineId,
          returnQuantity: row?.returnQuantity,
        });
      }
    });

    const payload = {
      returns: Object.entries(groupedByTrackingId)?.map(
        ([trackingId, medicines]) => ({
          trackingId,
          medicines,
        })
      ),
    };

    setLoading(true);
    const response = await addMedicineReturnApi(payload);
    setLoading(false);

    if (response?.code === 200 || response?.isSuccess) {
      toast.success(response?.message || `Medicine returned successfully`);
      setSelectedRows([]);
      refetch();
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  useEffect(() => {
    setMedReqTrackList(
      allData?.medicineRequests?.map((option) => ({
        ...option,
        isSelected: false,
        returnQuantity: 0,
      })) || []
    );
  }, [allData]);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              padding: 2,
              marginTop: 2,
              borderRadius: 2,
              marginRight: "90px",
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>
            {isLoading ? (
              <Loader />
            ) : isError ? (
              <ErrorMessage />
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 0,
                    mb: "20px",
                    color: "#5D6A82",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Open Sans",
                      fontWeight: 600,
                      fontSize: "20px",
                      lineHeight: "29px",
                      letterSpacing: "0px",
                      color: "inherit",
                    }}
                  >
                    Medicine Return
                  </Typography>
                </Box>

                {/* Patient Info */}
                <Box
                  sx={{
                    border: `1px solid #CCD4DA`,
                    borderRadius: "12px",
                    p: "20px",
                    mb: "30px",
                  }}
                >
              <Typography sx={{ color: "#5D6A82", mb: 2, fontSize: "14px" }}>
                Patient Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <TextField
                    label="UHID"
                    value={patientInfo?.uhid || ""}
                    fullWidth
                    required
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <TextField
                    label="Patient Name"
                    value={patientInfo?.patientName || ""}
                    fullWidth
                    required
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <TextField
                    label="Ward Name"
                    fullWidth
                    value={patientInfo?.wardName || ""}
                    required
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <TextField
                    label="Bed Number"
                    fullWidth
                    value={patientInfo?.bedNumber || ""}
                    required
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Invoice Number */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: "20px",
              }}
            >
              <Autocomplete
                options={medReqTrackList?.map(
                  (option) => option?.invoiceNumber || ""
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Invoice Number" />
                )}
                value={invoiceNumber || null}
                onChange={(_, newValue) => handleInvoiceChange(newValue)}
                sx={{ minWidth: "170px" }}
              />
              {/* <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Invoice Number: {patientInfo?.invoiceNumber || ""}
              </Typography> */}
            </Box>

            {/* Table */}
            <TableContainer component={Paper}>
              <Table aria-label="patient table">
                <TableHead
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": {},
                  }}
                >
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedRows?.length > 0 &&
                          selectedRows?.length < medReqTrackList?.length
                        }
                        checked={
                          medReqTrackList?.length > 0 &&
                          selectedRows?.length === medReqTrackList?.length
                        }
                        onChange={handleSelectAllClick}
                      />
                    </TableCell>
                    <TableCell>SNo</TableCell>
                    <TableCell>Item Code</TableCell>
                    <TableCell>Medicine Name</TableCell>

                    <TableCell>Issued Quantity</TableCell>
                    <TableCell>Requested Quantity</TableCell>
                    <TableCell>Return Quantity</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {medReqTrackList?.map((row, index) => {
                    return (
                      <TableRow
                        key={row?.sNo}
                        tabIndex={-1}
                        role="checkbox"
                        selected={row?.isSelected}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            disableRipple
                            checked={row?.isSelected}
                            onChange={(event) => handleClick(event, row)}
                          />
                        </TableCell>
                        <TableCell>{row?.sNo || "-"}</TableCell>
                        <TableCell>{row?.itemCode || "-"}</TableCell>
                        <TableCell>{row?.medicineName || "-"}</TableCell>
                        <TableCell>{row?.issuedQuantity || "0"}</TableCell>
                        <TableCell>{row?.requestedQuantity || "0"}</TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={row?.returnQuantity ?? ""}
                            onChange={(e) =>
                              handleReturnQuantityChange(
                                row?.sNo,
                                e.target.value,
                                row?.issuedQuantity
                              )
                            }
                            disabled={!row?.isSelected}
                            inputProps={{ min: 0, step: 1 }}
                          />
                        </TableCell>

                        <TableCell align="right">
                          <IconButton onClick={() => handleOpenModal(row)}>
                            <RemoveRedEyeIcon sx={{ fontSize: "18px" }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

                <Box sx={{ textAlign: "center", mt: "20px" }}>
                  <Button
                    variant="contained"
                    sx={{ textTransform: "none" }}
                    onClick={handleReturnMedicine}
                    disabled={
                      loading ||
                      !selectedRows?.some((row) => row?.returnQuantity > 0)
                    }
                  >
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      `Return Medicine`
                    )}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
        <MedViewDetails
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          details={selectedRow4ReturnDetails}
        />
      </Box>
    </Container>
  );
}
